import { useCallback, useEffect, useRef, useState } from 'react';
import { APP_VERSION } from '../generated/build-meta';
import { subscribeAppUpdate } from '../utils/app-update-events';
import { compareSemver } from '../utils/compareSemver';
import { fetchDeployedReleases, fetchDeployedVersion } from '../utils/fetch-deployed-version';

const DISMISS_KEY = 'nbco_update_dismiss';
const POLL_MS = 5 * 60 * 1000;

function releaseChanges(release) {
  if (!release) {
    return [];
  }
  if (Array.isArray(release.changes)) {
    return release.changes.map((line) => String(line).trim()).filter(Boolean);
  }
  return String(release.changes || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

export default function useAppUpdateCheck() {
  const [prompt, setPrompt] = useState(null);
  const swRegistrationRef = useRef(null);

  const showPrompt = useCallback((payload) => {
    setPrompt((prev) => {
      if (prev && prev.version && payload.version && compareSemver(payload.version, prev.version) < 0) {
        return prev;
      }
      return payload;
    });
  }, []);

  const checkVersionJson = useCallback(async () => {
    if (process.env.NODE_ENV !== 'production') {
      return;
    }

    try {
      const deployed = await fetchDeployedVersion();
      const serverVersion = deployed && deployed.version ? String(deployed.version) : '';

      if (!serverVersion || compareSemver(serverVersion, APP_VERSION) <= 0) {
        return;
      }

      if (localStorage.getItem(DISMISS_KEY) === serverVersion) {
        return;
      }

      let title = 'نسخه جدید در دسترس است';
      let changes = [];
      try {
        const releases = await fetchDeployedReleases();
        const release = releases.find((item) => item && item.version === serverVersion);
        if (release && release.title) {
          title = release.title;
        }
        changes = releaseChanges(release);
      } catch (e) {
        changes = [];
      }

      showPrompt({
        source: 'version',
        version: serverVersion,
        title,
        changes,
        registration: swRegistrationRef.current,
      });
    } catch (e) {
      // ignore network errors during background checks
    }
  }, [showPrompt]);

  useEffect(() => {
    checkVersionJson();

    const intervalId = window.setInterval(checkVersionJson, POLL_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkVersionJson();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    const unsubscribe = subscribeAppUpdate(({ registration }) => {
      swRegistrationRef.current = registration;
      if (localStorage.getItem(DISMISS_KEY) === 'sw-pending') {
        return;
      }
      showPrompt({
        source: 'sw',
        version: null,
        title: 'نسخه جدید آماده است',
        changes: ['برای استفاده از آخرین تغییرات، صفحه را بروزرسانی کنید.'],
        registration,
      });
    });

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
      unsubscribe();
    };
  }, [checkVersionJson, showPrompt]);

  const dismiss = useCallback(() => {
    if (!prompt) {
      return;
    }
    const key = prompt.version || 'sw-pending';
    localStorage.setItem(DISMISS_KEY, key);
    setPrompt(null);
  }, [prompt]);

  const applyUpdate = useCallback(() => {
    const registration = prompt && prompt.registration ? prompt.registration : swRegistrationRef.current;
    if (registration && registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
    if (prompt && prompt.version) {
      localStorage.removeItem(DISMISS_KEY);
    }
    window.location.reload();
  }, [prompt]);

  return {
    prompt,
    dismiss,
    applyUpdate,
  };
}
